/**
 * PHI-safe structured logger.
 *
 * Contract:
 *   - Never log raw patient data. Redact names, DOB, diagnoses, identifiers
 *     (including GUIDs in identifying contexts).
 *   - Log events + stable metadata (status, code, correlationId, duration).
 *   - Callers MUST NOT pass unfiltered request / response bodies.
 *
 * This logger is intentionally small. The console line is the primary sink;
 * `addLogBreadcrumb` additionally mirrors each event into Sentry as a breadcrumb
 * (see src/observability/sentry.ts) when a DSN is configured.
 */

import { addLogBreadcrumb } from '@/observability/sentry';
import { looksLikeUrl, scrubUrl } from '@/lib/scrubUrl';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogMeta = Record<string, unknown>;

const PHI_KEY_PATTERN =
  /(^|_)(first_?name|last_?name|full_?name|name|dob|date_?of_?birth|birthdate|ssn|mrn|email|phone|address|diagnosis|diagnoses|hpo|icd|omim|notes?|comments?|guid|patient_?id|person_?id)($|_)/i;

function normalizeKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

function isPhiKey(key: string): boolean {
  return PHI_KEY_PATTERN.test(normalizeKey(key));
}

const MAX_STRING_LEN = 256;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function redact(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[depth-limit]';
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    // URL/path-shaped values (e.g. an API `path` like `/persons/<guid>?mrn=…`)
    // carry identifiers in path segments and query strings. Mask ID segments
    // and drop the query before anything reaches the console or Sentry.
    const scrubbed = looksLikeUrl(value) ? scrubUrl(value) : value;
    return scrubbed.length > MAX_STRING_LEN
      ? `${scrubbed.slice(0, MAX_STRING_LEN)}…[truncated]`
      : scrubbed;
  }

  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => redact(item, depth + 1));
  }

  if (value instanceof Error) {
    return { name: value.name, message: redact(value.message, depth + 1) };
  }

  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = isPhiKey(key) ? '[redacted]' : redact(val, depth + 1);
    }
    return out;
  }

  return '[unserializable]';
}

function emit(level: LogLevel, event: string, meta?: LogMeta): void {
  const safeMeta = meta ? (redact(meta) as Record<string, unknown>) : undefined;

  // Feed Sentry as a breadcrumb (context for the next captured exception; does
  // not create an issue by itself). Uses the already-redacted meta so the PHI
  // rules are applied exactly once, here. No-op unless Sentry is configured.
  addLogBreadcrumb(level, event, safeMeta);

  const payload = {
    level,
    event,
    ts: new Date().toISOString(),
    ...(safeMeta ? { meta: safeMeta } : {}),
  };

  const sink =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : // eslint-disable-next-line no-console -- central sink; info/debug are intentional.
          console.info;

  // Single stringified line keeps CI logs grep-friendly and prevents objects
  // from accidentally dumping nested PHI via console pretty-printing.
  sink(JSON.stringify(payload));
}

export const logger = {
  debug: (event: string, meta?: LogMeta): void => {
    if (import.meta.env.DEV) emit('debug', event, meta);
  },
  info: (event: string, meta?: LogMeta): void => {
    emit('info', event, meta);
  },
  warn: (event: string, meta?: LogMeta): void => {
    emit('warn', event, meta);
  },
  error: (event: string, meta?: LogMeta): void => {
    emit('error', event, meta);
  },
};
